// `locales/*.json` are the editable source of truth for every string this app renders.
// Vite (and vitest, which shares its resolver) inlines them at build time, and
// `resolveJsonModule` in tsconfig.app.json is what lets tsc read them.
import en from './locales/en.json';
import ar from './locales/ar.json';
import { decimalsFor } from '@/lib/shared-types';

export const resources = {
  en: { translation: en },
  ar: { translation: ar },
} as const;

export type Locale = keyof typeof resources;

export interface LocaleMeta {
  code: Locale;
  name: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
}

/** RTL/LTR is decided here, once, for web and native alike. */
export const LOCALES: Record<Locale, LocaleMeta> = {
  en: { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  ar: { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
};

export const DEFAULT_LOCALE: Locale = 'en';

export function isRTL(locale: string): boolean {
  return LOCALES[locale as Locale]?.dir === 'rtl';
}

export function dirFor(locale: string): 'ltr' | 'rtl' {
  return isRTL(locale) ? 'rtl' : 'ltr';
}

/**
 * Formats money for display.
 *
 * Deliberately does not assume two decimal places — the minor unit comes from the ISO table in
 * `src/lib/shared-types` (`decimalsFor`), so KWD shows three and JPY none, and hardcoding 2
 * mis-states every amount in those venues. Deliberately does not round either: the API sends
 * decimal strings computed server-side with decimal.js, and re-deriving a figure in float here
 * is how a total on screen ends up disagreeing with the total on the bill.
 *
 * `Intl` needs a Number, so a value too large to represent exactly is rendered from the string
 * instead of quietly losing precision.
 */
export function formatCurrency(
  amount: number | string,
  currency = 'SAR',
  locale: string = DEFAULT_LOCALE,
): string {
  const digits = decimalsFor(currency);
  const intl = locale.startsWith('ar') ? 'ar-SA' : 'en-US';
  const raw = typeof amount === 'string' ? amount : String(amount);
  const value = Number(raw);

  if (!Number.isFinite(value)) {
    return new Intl.NumberFormat(intl, {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(0);
  }

  // Beyond 2^53 a Number cannot represent the string exactly; print the digits we were given.
  if (!Number.isSafeInteger(Math.trunc(value))) {
    return `${currency} ${raw}`;
  }

  return new Intl.NumberFormat(intl, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** Minor units for a currency code — re-exported so callers need one import. */
export { decimalsFor } from '@/lib/shared-types';

/**
 * The decimal string to send back to the API for a user-entered amount, clamped to the
 * currency's minor unit. Keeps a typed "12.3456" from becoming a validation error, and a KWD
 * entry at three places rather than two.
 */
export function toMoneyString(amount: number | string, currency = 'SAR'): string {
  const digits = decimalsFor(currency);
  const value = typeof amount === 'string' ? Number(amount) : amount;
  return Number.isFinite(value) ? value.toFixed(digits) : (0).toFixed(digits);
}

export function formatDateTime(value: string | Date, locale: string = DEFAULT_LOCALE): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

/** mm:ss / h:mm:ss for resource timers. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export { en, ar };
